/*
 * Copyright (c) 2018 Integral Agile, Inc. All rights reserved.
 *   Use of this file is subject to the terms and conditions defined in the file
 * 'LICENSE.txt'
 *   which is distributed in the root directory of this source code tree.
 */

import Types from '../constants/actionTypes';
import {
  addSteps,
  createTour,
  compareSteps,
  selectStepsByPredicate,
  autoShowSteps
} from '../tour/tourHelpers';
import organizationTeamsWall from '../tour/steps/organizationTeamsWall';
import organizationProductsWall from '../tour/steps/organizationProductsWall';
import organizationCard from '../tour/steps/organizationCard';
import featureListWall from '../tour/steps/featureListWall';
import storyListWall from '../tour/steps/storyListWall';

const stepDefinitions = {
  organizationTeamsWall,
  organizationProductsWall,
  organizationCard,
  featureListWall,
  storyListWall
};

const listWallSteps = [
  stepDefinitions.storyListWall,
  stepDefinitions.featureListWall,
  null,
  null
];

const configureOrgWallSteps = (showingProducts, action, state) => {
  const stepDefinition = showingProducts
    ? 'organizationProductsWall'
    : 'organizationTeamsWall';

  let steps = stepDefinitions[stepDefinition];

  if (steps) {
    steps = selectStepsByPredicate(steps, action.fullState);
    const autoShow = autoShowSteps(steps, action.fullState);
    const tour = createTour();
    addSteps(tour, steps);
    return {
      tour,
      stepDefinition,
      autoShow,
      stepCount: steps.length
    };
  }
  return { tour: state.mainTour, stepDefinition, stepCount: 0 };
};

const configureModalSteps = (modal, tab) => {
  const tour = createTour();
  let nextStep = null;
  if (stepDefinitions[modal])
    nextStep = addSteps(tour, stepDefinitions[modal], tab);
  return { tour, nextStep };
};

const configureListWallSteps = (action, state) => {
  // Compares the current list wall steps to the ones retrieved below using the predicate of each to select them.
  // Only if the array of step ids changes do we re-create the tour.
  // This way we can call this method when work packages are updated (or any other one that affects the predicates) efficiently.
  const level = action.level || state.level;

  let steps = listWallSteps[level - 1];
  if (steps) {
    steps = selectStepsByPredicate(steps, action.fullState);
    const autoShow = autoShowSteps(steps, action.fullState);
    const stepDefinition = `listWallLevel${level}`;
    const { changedOrAddedSteps, removedSteps } = compareSteps(
      steps,
      state.mainTour
    );
    if (changedOrAddedSteps.length + removedSteps.length !== 0) {
      // steps changed based on predicates
      const tour = createTour();
      addSteps(tour, steps);
      return {
        tour,
        stepDefinition,
        stepsAdded:
          level === state.level && changedOrAddedSteps.length > 0
            ? changedOrAddedSteps
            : null,
        autoShow,
        stepCount: steps.length
        // no new level was provided, so it was the steps that changed based on state.
        // we only care if steps were added or changed
      };
    }
    return {
      tour: state.mainTour,
      stepDefinition,
      autoShow,
      stepCount: steps.length
    };
  }
  return { tour: state.mainTour, stepDefinition: '', stepCount: 0 };
};

const initialState = {
  mainTour: process.env.NODE_ENV === 'test' ? null : createTour(),
  modalTour: process.env.NODE_ENV === 'test' ? null : createTour(),
  activeTour: 'mainTour',
  activeSteps: '',
  showing: null,
  currentStep: null,
  available: false,
  attention: false
};

const showingListWall = (action, state) => {
  state.mainTour.cancel();
  const { tour, stepDefinition, stepCount, stepsAdded, autoShow } =
    configureListWallSteps(action, state);
  let nextStep = null;
  if (autoShow?.length) [nextStep] = autoShow;
  else if (stepsAdded?.length) [nextStep] = stepsAdded;

  return {
    ...state,
    mainTour: tour,
    activeSteps: stepDefinition,
    available: stepCount > 0,
    attention: state.attention || stepsAdded || false, // maintain existing attention state if unchanged
    level: action.level || state.level,
    showing:
      stepDefinition.length > 0 &&
      (state.showing || autoShow?.length) &&
      tour.id,
    currentStep: nextStep,
    flippedListWallStep: null
  };
};

const showingOrgWall = (action, state) => {
  state.mainTour.cancel();
  const showingProducts =
    action.showingProducts === undefined // undefined on initial dispatch
      ? state.showingProducts
      : action.showingProducts;
  const { tour, stepDefinition, autoShow, stepCount } = configureOrgWallSteps(
    showingProducts,
    action,
    state
  );

  let nextStep = null;
  if (autoShow?.length) [nextStep] = autoShow;

  return {
    ...state,
    mainTour: tour,
    activeSteps: stepDefinition,
    available: stepCount > 0,
    attention: false,
    showing: (autoShow?.length || state.showing) && tour.id,
    showingProducts,
    flippedListWallStep: state.currentStep,
    currentStep: state.flippedListWallStep || nextStep,
    level: null
  };
};

const updateTourState = (action, state) => {
  if (
    state.activeTour === 'mainTour' &&
    state.activeSteps.startsWith('listWallLevel')
  ) {
    return showingListWall(action, state);
  }
  return state;
};

const tourReducer = (state = initialState, action) => {
  const { activeTour, currentStep } = state;
  const tour = activeTour ? state[activeTour] : null;

  let modalSteps;
  switch (action.type) {
    case Types.TOUR_ACTIVE:
      return { ...state, showing: tour.id, attention: false };
    case Types.TOUR_INACTIVE:
      return { ...state, showing: null };
    case Types.CLOSE_MODAL:
      state.modalTour.hide(); // the tour is outside the React DOM so we can do this here.
      return {
        ...state,
        activeTour: 'mainTour',
        attention: false,
        currentStep: state.mainCurrentStep,
        showing: state.mainShowing && state.mainTour.id
      };
    case Types.OPEN_MODAL:
      if (action.modalType.name === 'MESSAGE') return state;
      tour.hide();
      modalSteps = configureModalSteps(action.id, action.tab);
      return {
        ...state,
        modalTour: modalSteps.tour,
        activeTour: 'modalTour',
        attention: false, // for now, clear the attention flag. Later, may want to preserve it when modal opens.
        showing: state.showing && modalSteps.tour.id,
        mainCurrentStep: currentStep,
        mainShowing: state.showing,
        currentStep: modalSteps.nextStep
      };
    case Types.UPDATE_MODAL:
      tour.hide();
      modalSteps = configureModalSteps(action.id, action.tab);
      return {
        ...state,
        modalTour: modalSteps.tour,
        attention: false, // for now assume any tab switch invalidates the attention icon
        activeTour: 'modalTour',
        showing: state.showing && modalSteps.tour.id,
        currentStep: modalSteps.nextStep
      };
    case Types.SHOWING_LIST_WALL:
      return showingListWall(action, state);
    case Types.SHOWING_ORG_WALL:
      return showingOrgWall(action, state);
    case Types.SHOWING_TOUR_STEP:
      // tour.hide();
      return { ...state, currentStep: action.id };
    case Types.CLOSING_TOUR:
      return { ...state, showing: null };
    case Types.UPDATE_TOUR_STATE:
      return updateTourState(action, state);
    case Types.USER_LOGOUT:
      // tour.hide();
      return {
        ...state,
        activeTour: 'mainTour',
        activeSteps: '',
        showing: null,
        currentStep: null,
        available: false,
        attention: false
      };
    default:
      return state;
  }
};

export default tourReducer;
